import { CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { Activities } from "../../../../types/apiTypes";
import { StatusIcon } from "./StatusIcon";
import {
  accountsApi,
  campaignsApi,
  useAnalyseMutation,
  useDeleteCompaniesActivitiesMutation,
  useFindExternalDataRetryMutation,
  useGenerateAccountsPlanListMutation,
  useGeneratePlaybookForMembersMutation,
} from "../../../../api";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../../../../hooks";
import { Link } from "react-router-dom";
import { ActivitiesStatus, NotificationTypes } from "../../../../types/enums";
import { setIsFetchTableMembers } from "../../../../features/companySlice";
import { notificationLinkMapper } from "../../../../utils/notificationLinkMapper";

const messages = {
  in_progress: "in progress",
  failed: {
    scraping: "no contacts found",
    analysis: "failed",
    account_plan_generation: "failed",
    sdr_playbook_generation: "failed",
  },
  completed: {
    scraping: "update is ready",
    analysis: "analyzed",
    account_plan_generation: "generated",
    sdr_playbook_generation: "generated",
  },
};

const usersActivitiesMessages = {
  in_progress: {
    scraping: "started contacts search for",
    analysis: "started analysis for",
    account_plan_generation: "started Account Plan generating for",
    sdr_playbook_generation: "started Playbook generating for",
  },
  failed: {
    scraping: "no contacts found",
    analysis: "failed",
    account_plan_generation: "failed",
    sdr_playbook_generation: "failed",
  },
  completed: {
    scraping: "found new contacts for",
    analysis: "analyzed",
    account_plan_generation: "generated Account Plans for",
    sdr_playbook_generation: "generated Playbook for",
  },
};

export const NotificationBody = ({
  title,
  items,
  setShowList,
  campaign_id,
  type,
  isEditor,
  userEmail,
}: {
  title: string;
  items: Record<string, Activities[]>;
  setShowList: (list: Activities[]) => void;
  campaign_id: string;
  type: NotificationTypes;
  isEditor: boolean;
  userEmail: string;
}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [analyse, { isLoading: isAnalyze }] = useAnalyseMutation();
  const [findExternalDataRetry, { isLoading: isFindExternalData }] =
    useFindExternalDataRetryMutation();

  const [generateAccountsPlanList, { isLoading: isGeneratingAccountsPlan }] =
    useGenerateAccountsPlanListMutation();

  const [deleteActivities, { isLoading: isDeleteActivities }] =
    useDeleteCompaniesActivitiesMutation();

  const [generatePlaybookForMembers, { isLoading: isGeneratingPlaybook }] =
    useGeneratePlaybookForMembersMutation();

  useEffect(() => {
    (isAnalyze ||
      isFindExternalData ||
      isGeneratingAccountsPlan ||
      isGeneratingPlaybook) &&
      setIsLoading(true);
  }, [
    isAnalyze,
    isFindExternalData,
    isGeneratingAccountsPlan,
    isGeneratingPlaybook,
  ]);

  const values: Record<ActivitiesStatus, string> = {
    in_progress: messages.in_progress,
    failed: messages.failed[type],
    completed: messages.completed[type],
    queued: "",
    updating: "",
  };

  const usersValues: Record<ActivitiesStatus, string> = {
    in_progress: usersActivitiesMessages.in_progress[type],
    failed: usersActivitiesMessages.failed[type],
    completed: usersActivitiesMessages.completed[type],
    queued: "",
    updating: "",
  };

  const tryAgainHandler = async (account_ids: string[]) => {
    switch (type) {
      case NotificationTypes.scraping:
        await findExternalDataRetry({ campaign_id, account_ids });
        break;
      case NotificationTypes.analysis:
        await analyse({ account_ids, campaign_id });
        break;

      case NotificationTypes.account_plan_generation:
        await generateAccountsPlanList({ campaign_id, account_ids })
          .unwrap()
          .then(() => {
            dispatch(accountsApi.util.invalidateTags(["list-view"]));
            dispatch(campaignsApi.util.invalidateTags(["summary"]));
          });
        break;

      case NotificationTypes.sdr_playbook_generation:
        await generatePlaybookForMembers({
          campaign_id,
          ids: account_ids,
          include_all: false,
        })
          .unwrap()
          .then(() => {
            dispatch(accountsApi.util.invalidateTags(["list-view"]));
            dispatch(
              campaignsApi.util.invalidateTags([
                "summary",
                "company",
                "role-members",
              ]),
            );
            dispatch(setIsFetchTableMembers(true));
          });
        break;
    }

    dispatch(campaignsApi.util.invalidateTags(["activities"]));
  };
  const clearHandler = (account_ids: string[]) => {
    deleteActivities({
      type,
      account_ids,
      campaign_id,
    });
  };

  const ActivitiesItem = ({
    status,
    companies,
    setShowList,
    type,
  }: {
    status: ActivitiesStatus;
    companies: Activities[];
    setShowList: (list: Activities[]) => void;
    type: NotificationTypes;
  }) => {
    if (!companies || companies.length === 0) return null;

    const url = notificationLinkMapper(type, campaign_id, companies[0].id);

    return (
      <Stack flexDirection="row" gap="8px" alignItems="center">
        <StatusIcon status={status} />
        {companies.length === 1 ? (
          <>
            {userEmail === companies[0].user.email ? (
              <Typography variant="body1" color="#1F2932">
                <Link
                  to={url}
                  style={{ textDecoration: "none", color: "#1F2932" }}
                >
                  {companies[0].name}
                </Link>{" "}
                {values[status]}
              </Typography>
            ) : (
              <Typography variant="body1" color="#1F2932">
                <span style={{ fontWeight: 600 }}>
                  {`${companies[0].user.first_name} ${companies[0].user.last_name} `}
                </span>{" "}
                {usersValues[status]}{" "}
                <Link
                  to={url}
                  style={{ textDecoration: "none", color: "#1F2932" }}
                >
                  {companies[0].name}
                </Link>
              </Typography>
            )}
          </>
        ) : (
          <>
            {userEmail === companies[0].user.email ? (
              <Typography variant="body1" color="#1F2932">
                <Link
                  to={url}
                  style={{ textDecoration: "none", color: "#1F2932" }}
                >
                  {companies[0].name}
                </Link>{" "}
                and{" "}
                <span
                  onClick={() => setShowList(companies)}
                  style={{
                    cursor: "pointer",
                    color: "#4B89BC",
                    textDecoration: "underline",
                  }}
                >
                  {companies.length - 1} more
                </span>{" "}
                {values[status]}
              </Typography>
            ) : (
              <Typography variant="body1" color="#1F2932">
                <span style={{ fontWeight: 600 }}>
                  {`${companies[0].user.first_name} ${companies[0].user.last_name} `}
                </span>{" "}
                {usersValues[status]}{" "}
                <Link
                  to={url}
                  style={{ textDecoration: "none", color: "#1F2932" }}
                >
                  {companies[0].name}
                </Link>{" "}
                and{" "}
                <span
                  onClick={() => setShowList(companies)}
                  style={{
                    cursor: "pointer",
                    color: "#4B89BC",
                    textDecoration: "underline",
                  }}
                >
                  {companies.length - 1} more
                </span>{" "}
              </Typography>
            )}
          </>
        )}
        <Stack
          flexDirection="row"
          gap="12px"
          alignItems="center"
          marginLeft="auto"
        >
          {status === "failed" && isEditor && (
            <Stack>
              {isLoading ? (
                <CircularProgress
                  size={20}
                  sx={{ color: "#3279B3", flexShrink: 0 }}
                />
              ) : (
                <span
                  onClick={() => tryAgainHandler(companies.map(({ id }) => id))}
                  style={{
                    cursor: "pointer",
                    color: "#1B3967",
                    textDecoration: "underline",
                    textWrap: "nowrap",
                  }}
                >
                  Try Again
                </span>
              )}
            </Stack>
          )}
          {isEditor && (
            <Stack>
              {isDeleteActivities ? (
                <CircularProgress
                  size={20}
                  sx={{ color: "#3279B3", flexShrink: 0 }}
                />
              ) : (
                <>
                  {status !== "in_progress" && (
                    <span
                      onClick={() =>
                        clearHandler(companies.map(({ id }) => id))
                      }
                      style={{
                        cursor: "pointer",
                        color: "#1F293299",
                        textDecoration: "underline",
                      }}
                    >
                      Clear
                    </span>
                  )}
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack flexGrow={1} padding="12px 10px" gap="8px">
      <Stack flexDirection="row" gap="4px" alignItems="center" flexGrow={1}>
        <Typography variant="body1" color="#1F293299">
          {title}
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Stack>
      {["in_progress", "failed", "completed"].map((el) => (
        <ActivitiesItem
          key={el}
          status={el as ActivitiesStatus}
          companies={items[el]}
          setShowList={setShowList}
          type={type}
        />
      ))}
    </Stack>
  );
};
